import * as React from 'react';

export const useOverlayAccessibility = (
	open: boolean,
	onClose: () => void,
	panelRef: React.RefObject<HTMLDivElement | null>
) => {
	const previousFocusRef = React.useRef<HTMLElement | null>(null);

	// Escape key closes the overlay
	React.useEffect(() => {
		if (!open) return undefined;

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [onClose, open]);

	// Save focus on open; restore it on close
	React.useEffect(() => {
		if (open) {
			previousFocusRef.current = document.activeElement as HTMLElement;
			const timer = setTimeout(() => {
				const panel = panelRef.current;
				if (!panel) return;
				const first = panel.querySelector<HTMLElement>(
					'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
				);
				first?.focus();
			}, 60);
			return () => clearTimeout(timer);
		}

		previousFocusRef.current?.focus();
		previousFocusRef.current = null;
	}, [open, panelRef]);

	// Focus trap — keep keyboard focus inside the dialog while it is open
	React.useEffect(() => {
		if (!open) return;

		const handleTabKey = (e: KeyboardEvent) => {
			if (e.key !== 'Tab') return;
			const panel = panelRef.current;
			if (!panel) return;

			const focusable = Array.from(
				panel.querySelectorAll<HTMLElement>(
					'button:not([disabled]), [href]:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
				)
			).filter((el) => !el.closest('[aria-hidden="true"]'));

			if (focusable.length === 0) return;

			const first = focusable[0];
			const last = focusable[focusable.length - 1];

			if (e.shiftKey) {
				if (document.activeElement === first) {
					e.preventDefault();
					last.focus();
				}
			} else {
				if (document.activeElement === last) {
					e.preventDefault();
					first.focus();
				}
			}
		};

		window.addEventListener('keydown', handleTabKey);
		return () => window.removeEventListener('keydown', handleTabKey);
	}, [open, panelRef]);
};
